import React, { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';

const C = {
  primary: '#0F6E56', primaryBg: '#E1F5EE', border: '#E3E1D9',
  text: '#1F1E1B', muted: '#5F5E5A', surface: '#F7F5F0',
  gold: '#91600C', danger: '#C0392B',
};

const API = '/api/freelance';

function authHeaders() {
  const t = localStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Token ${t}` } : {}) };
}

const btn = {
  fontSize: 11, padding: '4px 10px', border: `1px solid ${C.border}`,
  borderRadius: 6, background: '#fff', cursor: 'pointer', color: C.muted,
};

const STATUS_LABEL = {
  pending: 'Not started',
  in_progress: 'In progress',
  delivered: 'Delivered — waiting for you',
  accepted: 'Paid',
  revision: 'Changes requested',
  cancelled: 'Cancelled',
};

const STATUS_COLOR = {
  pending: C.muted, in_progress: C.gold, delivered: C.primary,
  accepted: C.primary, revision: C.danger, cancelled: C.muted,
};

/**
 * Order ke milestones - poori raqam pehle se escrow mein hai,
 * har hissa accept hone par sirf us ka paisa release hota hai.
 */
export default function Milestones({ orderId }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/orders/${orderId}/milestones/`, { headers: authHeaders() })
      .then((r) => r.json()).then(setD).catch(() => {});
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  if (!d || d.error || !d.count) return null;

  const act = async (m, action) => {
    let note = '';
    if (action === 'deliver') {
      note = window.prompt('Delivery note (what did you do?)') || '';
    } else if (action === 'revision') {
      note = window.prompt('What needs changing?');
      if (note === null) return;
    } else if (action === 'accept') {
      const pay = (m.amount_pi * 0.95).toFixed(4);
      if (!window.confirm(`Accept "${m.title}"?\n\n${pay} Pi will be released to the freelancer (after the 5% fee).\n\nThis cannot be undone.`)) return;
    }

    setBusy(true);
    try {
      const res = await fetch(`${API}/milestones/${m.id}/action/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ action, note }),
      });
      const j = await safeJson(res);
      if (!res.ok) throw new Error(j.error || 'Failed');
      load();
    } catch (e) { alert(networkMessage(e)); }
    setBusy(false);
  };

  const pct = d.count ? Math.round((d.done / d.count) * 100) : 0;

  return (
    <div style={{ marginTop: 8, border: `1px solid ${C.border}`, borderRadius: 9, overflow: 'hidden' }}>
      <div style={{ background: C.surface, padding: '7px 10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700 }}>
            {'\u{1F3AF}'} Milestones {d.done}/{d.count}
          </span>
          <span style={{ fontSize: 10.5, color: C.muted }}>
            {d.order_status === 'pending' ? (
              <b style={{ color: C.gold }}>Not paid yet — {d.total_pi} Pi</b>
            ) : (
              <>
                <b style={{ color: C.primary }}>{d.released_pi} Pi</b> released ·
                {' '}<b style={{ color: C.gold }}>{d.still_held_pi} Pi</b> in escrow
              </>
            )}
          </span>
        </div>
        <div style={{ background: '#fff', borderRadius: 4, height: 5, overflow: 'hidden', marginTop: 5 }}>
          <div style={{ width: `${pct}%`, height: '100%', background: C.primary, transition: 'width .4s' }} />
        </div>
      </div>

      <div style={{ background: '#fff', padding: '6px 10px' }}>
        {d.milestones.map((m) => (
          <div key={m.id} style={{ padding: '7px 0', borderBottom: `1px solid ${C.surface}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                {m.status === 'accepted' ? '\u2705 ' : `${m.number}. `}{m.title}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: C.primary }}>{m.amount_pi} Pi</span>
            </div>

            {m.description && (
              <p style={{ margin: '2px 0', fontSize: 11, color: C.muted }}>{m.description}</p>
            )}

            <p style={{ margin: '2px 0', fontSize: 10.5, color: STATUS_COLOR[m.status] || C.muted }}>
              {STATUS_LABEL[m.status] || m.status}
              {m.delivery_days ? ` · ${m.delivery_days} days` : ''}
              {m.is_late ? <span style={{ color: C.danger, fontWeight: 700 }}> · LATE</span> : ''}
              {m.status === 'accepted' && m.payout_pi ? ` · ${m.payout_pi} Pi paid` : ''}
            </p>

            {m.delivery_note && (
              <p style={{ margin: '3px 0', fontSize: 11, color: C.text, background: C.primaryBg,
                          padding: '4px 7px', borderRadius: 5 }}>
                {m.delivery_note}
              </p>
            )}
            {m.revision_note && m.status === 'revision' && (
              <p style={{ margin: '3px 0', fontSize: 11, color: C.danger }}>
                Changes: {m.revision_note}
              </p>
            )}

            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 5 }}>
              {d.is_seller && (m.status === 'pending' || m.status === 'revision') && (
                <button style={btn} disabled={busy} onClick={() => act(m, 'start')}>Start</button>
              )}
              {d.is_seller && ['in_progress', 'revision', 'pending'].includes(m.status) && (
                <button style={{ ...btn, color: C.primary, borderColor: C.primary, fontWeight: 600 }}
                        disabled={busy} onClick={() => act(m, 'deliver')}>
                  Deliver
                </button>
              )}
              {d.is_buyer && m.status === 'delivered' && (
                <>
                  <button style={{ ...btn, background: C.primary, color: '#fff', borderColor: C.primary, fontWeight: 700 }}
                          disabled={busy} onClick={() => act(m, 'accept')}>
                    Accept &amp; pay {(m.amount_pi * 0.95).toFixed(2)} Pi
                  </button>
                  <button style={{ ...btn, color: C.danger, borderColor: C.danger }}
                          disabled={busy} onClick={() => act(m, 'revision')}>
                    Ask for changes
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Offer bhejte waqt milestones ka khaka. */
export function MilestoneBuilder({ rows, setRows, totalPrice }) {
  const add = () => setRows([...rows, { title: '', amount_pi: '', delivery_days: 3 }]);
  const del = (i) => setRows(rows.filter((_, x) => x !== i));
  const upd = (i, k, v) => setRows(rows.map((r, x) => (x === i ? { ...r, [k]: v } : r)));

  const sum = rows.reduce((a, r) => a + (parseFloat(r.amount_pi) || 0), 0);
  const price = parseFloat(totalPrice) || 0;
  const ok = rows.length === 0 || Math.abs(sum - price) < 0.01;

  const inp = { padding: '6px 8px', fontSize: 12, border: `1px solid ${C.border}`, borderRadius: 6 };

  return (
    <div style={{ background: '#fff', borderRadius: 7, padding: 8, marginBottom: 7 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700 }}>{'\u{1F3AF}'} Milestones (optional)</span>
        <button onClick={add} style={btn}>+ Add</button>
      </div>

      {rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: 10.5, color: C.muted }}>
          For bigger jobs — split the work into stages. The client escrows the full
          amount once, and each stage is paid out as you finish it.
        </p>
      ) : (
        <>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 5, marginBottom: 5, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: C.muted, width: 12 }}>{i + 1}</span>
              <input value={r.title} onChange={(e) => upd(i, 'title', e.target.value)}
                placeholder="Stage title" style={{ ...inp, flex: 2 }} />
              <input value={r.amount_pi} onChange={(e) => upd(i, 'amount_pi', e.target.value)}
                type="number" step="0.01" placeholder="Pi" style={{ ...inp, width: 68 }} />
              <input value={r.delivery_days} onChange={(e) => upd(i, 'delivery_days', e.target.value)}
                type="number" placeholder="days" style={{ ...inp, width: 56 }} />
              <button onClick={() => del(i)} style={{ ...btn, color: C.danger, padding: '4px 7px' }}>
                {'\u00d7'}
              </button>
            </div>
          ))}
          <p style={{ margin: '3px 0 0', fontSize: 10.5, color: ok ? C.muted : C.danger, fontWeight: ok ? 400 : 700 }}>
            Total {sum.toFixed(2)} Pi{price ? ` of ${price.toFixed(2)} Pi` : ''}
            {!ok && ' — these must add up to the price'}
          </p>
        </>
      )}
    </div>
  );
}
